/**
 * Summer or winter operation, and how a trail shows in each.
 *
 * Twin of App\Support\Winter\WinterSeason — same vocabulary, same
 * answer — so a map the server builds and a map the phone builds agree
 * about which trails are greyed. `wintergrooming_verify` runs both
 * against the same fixture rather than comparing the two by eye.
 *
 * The season comes from `page.props.workspace.season` (or a public
 * payload's own `season`), already resolved against the Winter grooming
 * module: with the module off it is always summer.
 */

export const SUMMER = 'summer';
export const WINTER = 'winter';

export const ROLE_GROOMED = 'groomed';
export const ROLE_OPEN = 'open';
export const ROLE_CLOSED = 'closed';
export const ROLES = [ROLE_GROOMED, ROLE_OPEN, ROLE_CLOSED];

export const ROLE_LABELS = {
    groomed: 'Groomed',
    open: 'Open, not groomed',
    closed: 'Closed for winter',
};

export const GROOMING_TYPES = {
    classic: 'Classic',
    skate: 'Skate',
    classic_skate: 'Classic + skate',
    fatbike: 'Fat bike',
    snowshoe: 'Snowshoe packed',
    multi: 'Multi-use packed',
};

export const STATE_HIDDEN = 'hidden';
export const STATE_CLOSED = 'closed';

/** The grey a closed-for-winter trail draws in, on every map. */
export const CLOSED_COLOR = '#9ca3af';
export const CLOSED_LABEL = 'Closed for winter';

/** Is this season string winter? Anything else, missing included, is summer. */
export function isWinter(season) {
    return season === WINTER;
}

/** The season off a workspace prop (or anything carrying `season`). */
export function seasonOf(holder) {
    return isWinter(holder?.season) ? WINTER : SUMMER;
}

/** A trail's winter role, with "not set" resolved to open. */
export function roleOf(trail) {
    const r = trail?.winter_role;
    return ROLES.includes(r) ? r : ROLE_OPEN;
}

/**
 * How a trail shows. null in an ordinary summer; 'hidden' for a
 * winter-only trail in summer; the role itself in winter.
 *
 * A payload that already carries `season_state` (the server worked it
 * out) is trusted only for the season it was computed in — so a phone
 * whose cached trails predate a season switch re-derives from the role
 * rather than painting last season's map.
 */
export function stateOf(trail, season) {
    if (!isWinter(season)) {
        return trail?.winter_only ? STATE_HIDDEN : null;
    }
    return roleOf(trail);
}

/** Should this trail be drawn at all in this season? */
export function isShown(trail, season) {
    return stateOf(trail, season) !== STATE_HIDDEN;
}

/** Is this trail greyed as closed for the winter? */
export function isClosedForWinter(trail, season) {
    return stateOf(trail, season) === STATE_CLOSED;
}

/** The colour a map draws this trail in: its own, or the winter grey. */
export function lineColor(trail, season, color) {
    return isClosedForWinter(trail, season) ? CLOSED_COLOR : color;
}

/** Line opacity to go with lineColor — a closed trail recedes. */
export function lineOpacity(trail, season, opacity = 0.9) {
    return isClosedForWinter(trail, season) ? 0.55 : opacity;
}
